<div style="max-width: 900px; margin: 30px auto;">
    <mat-card class="mb-20">
        <mat-card-header>
            <mat-card-title>Book Physical Appointment</mat-card-title>
            <mat-card-subtitle>Schedule an appointment for a patient present in the hospital.</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="pt-20">
            <!-- Patient Search -->
            <div class="mb-20">
                <app-patient-filter></app-patient-filter>
            </div>

            <!-- Booking Form -->
            <form [formGroup]="bookingForm" (ngSubmit)="submitAppointment()" *ngIf="selectedPatientId">
                <div class="g-just-flex gap-20" style="align-items: flex-start; flex-wrap: wrap;">
                    
                    <mat-form-field appearance="outline" style="flex: 1; min-width: 250px;">
                        <mat-label>Select Practitioner</mat-label>
                        <input type="text" matInput [formControl]="practitionerSearchCtrl" [matAutocomplete]="practitionerAuto" placeholder="Search doctor...">
                        <mat-autocomplete #practitionerAuto="matAutocomplete" [displayWith]="displayPractitionerFn" (optionSelected)="onPractitionerSelected($event)">
                            @if(loadingPractitioners) {
                                <mat-option disabled>Loading practitioners...</mat-option>
                            } @else {
                                @for(p of filteredPractitioners; track p.id) {
                                    <mat-option [value]="p">
                                        <div style="display: flex; flex-direction: column; line-height: normal; padding: 8px 0;">
                                            <span>{{ p.name }}</span>
                                            <span style="font-size: 0.8em; color: var(--mat-sys-on-surface-variant); opacity: 0.8;">{{ p.roleDisplay }}</span>
                                        </div>
                                    </mat-option>
                                }
                            }
                        </mat-autocomplete>
                    </mat-form-field>

                    <mat-form-field appearance="outline" style="flex: 1; min-width: 250px;">
                        <mat-label>Task / Appointment Type</mat-label>
                        <mat-select formControlName="appointmentType">
                            @for(type of allowedAppointmentTypes; track type.code) {
                                <mat-option [value]="type.code">
                                    {{ type.display }}
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>

                    <mat-form-field appearance="outline" style="flex: 1; min-width: 250px;">
                        <mat-label>Pick Date</mat-label>
                        <input matInput [matDatepicker]="picker" formControlName="date">
                        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                        <mat-datepicker #picker></mat-datepicker>
                    </mat-form-field>
                </div>

                <!-- Availability Summary -->
                @if(bookingForm.get('practitioner')?.value && bookingForm.get('date')?.value) {
                    @if(!loadingSchedule && availableBlocks.length > 0) {
                        <div class="mt-20 p-15" style="background: var(--mat-sys-surface-container-low); border-radius: 8px; border: 1px solid var(--mat-sys-outline-variant);">
                            <div class="small-subtitle mb-10" style="color: var(--mat-sys-on-surface); font-weight: 500;">
                                <mat-icon style="vertical-align: bottom; font-size: 18px; width: 18px; height: 18px; margin-right: 4px;">schedule</mat-icon> 
                                Available Time Blocks Today
                            </div>
                            <mat-chip-set>
                                @for(block of availableBlocks; track block.startMs) {
                                    <mat-chip highlighted style="pointer-events: none;">
                                        {{ block.startStr }} - {{ block.endStr }}
                                    </mat-chip>
                                }
                            </mat-chip-set>
                        </div>
                    } @else if(!loadingSchedule && shiftBlocks.length === 0) {
                        <div class="mt-20 p-15" style="background: var(--mat-sys-error-container); border-radius: 8px;">
                            <div class="small-subtitle" style="color: var(--mat-sys-on-error-container); display: flex; align-items: center; gap: 8px;">
                                <mat-icon>event_busy</mat-icon> Practitioner is not scheduled to work on this day.
                            </div>
                        </div>
                        <div class="mt-15 g-just-flex" style="justify-content: flex-end;">
                            <button mat-stroked-button color="accent" type="button" (click)="sendReminderForSelectedDay()">
                                <mat-icon>notifications</mat-icon>
                                Remind day
                            </button>
                        </div>
                    } @else if(!loadingSchedule && shiftBlocks.length > 0 && availableBlocks.length === 0) {
                        <div class="mt-20 p-15" style="background: var(--mat-sys-error-container); border-radius: 8px;">
                            <div class="small-subtitle" style="color: var(--mat-sys-on-error-container); display: flex; align-items: center; gap: 8px;">
                                <mat-icon>event_busy</mat-icon> Practitioner is fully booked for this day.
                            </div>
                        </div>
                        <div class="mt-15 g-just-flex" style="justify-content: flex-end;">
                            <button mat-stroked-button color="accent" type="button" (click)="sendReminderForSelectedDay()">
                                <mat-icon>notifications</mat-icon>
                                Remind day
                            </button>
                        </div>
                    }
                }

                <!-- Time and Duration Selection -->
                @if(bookingForm.get('practitioner')?.value && bookingForm.get('appointmentType')?.value && bookingForm.get('date')?.value) {
                    <div class="mt-20 g-just-flex gap-20" style="align-items: flex-start; flex-wrap: wrap;">
                        <mat-form-field appearance="outline" style="flex: 1; min-width: 200px;">
                            <mat-label>Start Time</mat-label>
                            <input matInput type="time" formControlName="time">
                            <mat-hint>Tip: Press 'A' or 'P' to select AM/PM</mat-hint>
                        </mat-form-field>
                        
                        <mat-form-field appearance="outline" style="flex: 1; min-width: 200px;">
                            <mat-label>Duration (Minutes)</mat-label>
                            <input matInput type="number" formControlName="duration" min="1">
                        </mat-form-field>
                    </div>

                    @if(loadingSchedule) {
                        <div class="small-subtitle mt-10" style="color: var(--mat-sys-on-surface-variant)">Checking schedule...</div>
                    } @else if(overlapError) {
                        <div class="small-subtitle mt-10" style="color: var(--mat-sys-error); display: flex; align-items: center; gap: 8px;">
                            <mat-icon>warning</mat-icon> {{ overlapError }}
                        </div>
                    }
                }

                <div class="mt-30 g-just-flex" style="justify-content: flex-end;">
                    <button mat-button type="button" routerLink="/app/appointments" class="me-10">Cancel</button>
                    <button mat-flat-button color="primary" type="submit" [disabled]="bookingForm.invalid || !selectedPatientId">
                        Confirm Appointment
                    </button>
                </div>
            </form>
        </mat-card-content>
    </mat-card>
</div>